import { useEffect, useRef } from 'react';

const REVEAL_FIELD_EVENT = 'declaration:reveal-field';

/**
 * Asks whichever card hides a field - behind an inactive tab, a collapsed section - to show it, so
 * that navigation can find the field in the DOM.
 *
 * Returns true when a card took the request. What it shows only renders on the next pass, so the
 * caller has to wait for that before looking the field up.
 */
export const requestFieldReveal = (path: string): boolean => {
    const event = new CustomEvent<string>(REVEAL_FIELD_EVENT, { detail: path, cancelable: true });

    return !window.dispatchEvent(event);
};

/**
 * Subscribes a card to reveal requests. The handler returns true when the path is its own and it
 * is about to show it.
 */
export const useRevealDeclarationField = (handler: (path: string) => boolean) => {
    // Kept in a ref so the listener is attached once, yet always sees the card's latest state.
    const handlerRef = useRef(handler);
    handlerRef.current = handler;

    useEffect(() => {
        const listener = (event: Event) => {
            if (handlerRef.current((event as CustomEvent<string>).detail)) event.preventDefault();
        };

        window.addEventListener(REVEAL_FIELD_EVENT, listener);

        return () => window.removeEventListener(REVEAL_FIELD_EVENT, listener);
    }, []);
};
